import type { BulkOperationDetail, BulkOperationRow } from '../model/bulkOperations';
import { fixtureBulkOperations } from './bulkOperations';

const storageKey = 'heyday.fixtures.bulk-operations.v1';
const defaultIds = new Set(fixtureBulkOperations.map((job) => job.id));
const savedJobs = new Map<string, BulkOperationDetail>();
const jobStatuses = new Set(['DRAFT', 'VALIDATED', 'QUEUED', 'PROCESSING', 'COMPLETED', 'COMPLETED_WITH_ERRORS', 'FAILED', 'CANCELLED']);
const rowStatuses = new Set(['PENDING', 'VALID', 'INVALID', 'PROCESSING', 'SUCCEEDED', 'FAILED', 'SKIPPED']);
const record = (value: unknown): value is Record<string, unknown> => Boolean(value && typeof value === 'object' && !Array.isArray(value));
const count = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 10_000;
const optionalText = (value: unknown): value is string | undefined => value === undefined || typeof value === 'string';

function fixtureDetail(value: unknown): BulkOperationDetail | undefined {
  if (!record(value) || typeof value.id !== 'string' || (!value.id.startsWith('fixture-') && !defaultIds.has(value.id))
    || typeof value.operationType !== 'string' || !value.operationType
    || typeof value.status !== 'string' || !jobStatuses.has(value.status)
    || typeof value.createdAt !== 'string' || !Number.isFinite(Date.parse(value.createdAt))
    || !count(value.totalRows) || !count(value.validRows) || !count(value.succeededRows) || !count(value.failedRows)
    || !optionalText(value.originalFilename) || !optionalText(value.validatedAt) || !optionalText(value.submittedAt) || !optionalText(value.completedAt)
    || !Array.isArray(value.rows) || value.rows.length > 10_000) return undefined;
  const rows: BulkOperationRow[] = [];
  const rowNumbers = new Set<number>();
  for (const row of value.rows) {
    if (!record(row) || !count(row.rowNumber) || row.rowNumber === 0 || rowNumbers.has(row.rowNumber)
      || typeof row.status !== 'string' || !rowStatuses.has(row.status)
      || !optionalText(row.errorCode) || !optionalText(row.errorMessage)
      || !optionalText(row.responseResourceType) || !optionalText(row.responseId)) return undefined;
    rowNumbers.add(row.rowNumber);
    rows.push({ rowNumber: row.rowNumber, status: row.status, errorCode: row.errorCode,
      errorMessage: row.errorMessage, responseResourceType: row.responseResourceType, responseId: row.responseId });
  }
  // 입력 원문이나 구성원 정보를 보관하지 않고 샘플 처리 결과 계약만 복사합니다.
  return {
    id: value.id, operationType: value.operationType, status: value.status, createdAt: value.createdAt,
    originalFilename: value.originalFilename, totalRows: value.totalRows, validRows: value.validRows,
    succeededRows: value.succeededRows, failedRows: value.failedRows,
    validatedAt: value.validatedAt, submittedAt: value.submittedAt, completedAt: value.completedAt, rows,
  };
}

function storedJobs(): BulkOperationDetail[] {
  try {
    if (typeof window === 'undefined') return [];
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(fixtureDetail).filter((job): job is BulkOperationDetail => Boolean(job));
  } catch {
    return [];
  }
}

/** fixtureMode 경로에서만 사용합니다. 실제 API 응답은 이 저장소에 전달하지 않습니다. */
export function listFixtureBulkOperations(): BulkOperationDetail[] {
  const jobs = new Map<string, BulkOperationDetail>();
  for (const job of [...fixtureBulkOperations.map((job) => ({ ...job, rows: job.rows ?? [] })), ...storedJobs(), ...savedJobs.values()]) {
    const detail = fixtureDetail(job);
    if (detail) jobs.set(detail.id, detail);
  }
  return [...jobs.values()].sort((left, right) => right.createdAt.localeCompare(left.createdAt));
}

export function getFixtureBulkOperation(id: string): BulkOperationDetail | undefined {
  return listFixtureBulkOperations().find((job) => job.id === id);
}

export function saveFixtureBulkOperation(value: BulkOperationDetail): BulkOperationDetail {
  const detail = fixtureDetail(value);
  if (!detail) throw new Error('유효한 샘플 작업만 저장할 수 있습니다.');
  savedJobs.set(detail.id, detail);
  try {
    if (typeof window !== 'undefined') window.sessionStorage.setItem(storageKey, JSON.stringify(listFixtureBulkOperations()));
  } catch {
    // 저장소를 사용할 수 없는 브라우저에서도 현재 화면의 샘플 탐색은 유지합니다.
  }
  return fixtureDetail(detail)!;
}
